import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { LearnDeckContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

/**
 * Skip for the Learn deck's current card, committed through the deck like the front menu's skip:
 * a lesson is skipped (`skippedAt`) and a review is set aside without a rating. A panel showing a
 * different morpheme than the card on show keeps the standalone skip.
 */
export function LearnDeckSkipCardButton({
  disabled,
  fallbackOnPress,
  front,
  isLoading,
}: {
  disabled: boolean;
  fallbackOnPress: () => void;
  front: string;
  isLoading: boolean;
}) {
  const [commit, currentItem, isTransitioning] = useContextSelectors(
    LearnDeckContext,
    (c) => c.commit,
    (c) => c.state.current.item,
    (c) => c.isTransitioning,
  );
  const isDeckCard =
    (currentItem.kind === "lesson" || currentItem.kind === "review") &&
    currentItem.grapheme === front;

  return (
    <SkipCardButton
      disabled={disabled || (isDeckCard && isTransitioning)}
      isLoading={isLoading}
      onPress={() => {
        if (!isDeckCard) {
          fallbackOnPress();
          return;
        }
        // A pronunciation-level lesson marks nothing: skipping it just moves on.
        commit(
          currentItem.kind === "lesson" && currentItem.part === undefined
            ? { kind: "skip-lesson" }
            : { kind: "continue" },
          "button",
        );
      }}
    />
  );
}

export function SkipCardButton({
  disabled,
  isLoading,
  onPress,
}: {
  disabled: boolean;
  isLoading: boolean;
  onPress: () => void;
}) {
  return (
    <Button
      disabled={disabled}
      isLoading={isLoading}
      onPress={onPress}
      testID="morpheme-info-skip-card"
      variant="secondary"
    >
      <Icon className="text-secondary-foreground" name="LuSkipForward" size={18} />
      <Text>Skip card</Text>
    </Button>
  );
}
